import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const checkSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/auth/session`, { withCredentials: true });
      setAuthenticated(Boolean(response.data.authenticated));
    } catch (_) {
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        if (error.response?.status === 401 && url.includes('/admin/') && !url.endsWith('/admin/auth/login')) {
          setAuthenticated(false);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const login = async (password) => {
    await axios.post(`${API}/admin/auth/login`, { password }, { withCredentials: true });
    setAuthenticated(true);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`, {}, { withCredentials: true });
    } finally {
      setAuthenticated(false);
    }
  };

  const timeParams = useMemo(() => ({
    range,
    ...(range === 'custom' ? { start_date: customStart, end_date: customEnd } : {}),
  }), [customEnd, customStart, range]);

  return (
    <AdminAuthContext.Provider value={{
      authenticated,
      loading,
      login,
      logout,
      checkSession,
      range,
      setRange,
      customStart,
      setCustomStart,
      customEnd,
      setCustomEnd,
      timeParams,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return context;
}
